import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogClose,
  MatDialogRef,
} from '@angular/material/dialog';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { NgFor } from '@angular/common';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import moment from 'moment-timezone';
import { uiUserSettingsDateFormatMap } from '../../data-access/consts/user-account-settings.const';
import { UserSettingsModels, UserModels } from '@share-models';
import {
  UserActions,
  UserSettingsActions,
} from '../../data-access/state/user-account-settings.actions';
import { Store } from '@ngrx/store';
import { UserAccountSettingsState } from '../../data-access/state/user-account-settings.reducer';
import {
  EnableEscCloseDirective,
  MatSelectBottomSheetDirective,
  UiButtonComponent,
} from '@ui';

@Component({
  selector: 'app-change-user-date-settings-modal',
  standalone: true,
  imports: [
    TranslatePipe,
    NgFor,
    MatSelect,
    MatDialogClose,
    MatOption,
    MatRadioButton,
    MatRadioGroup,
    ReactiveFormsModule,
    UiButtonComponent,
    MatSelectBottomSheetDirective,
  ],
  templateUrl: './change-user-date-settings-modal.component.html',
  styleUrl: './change-user-date-settings-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [EnableEscCloseDirective],
})
export class ChangeUserDateSettingsModalComponent implements OnInit {
  private fb = inject(FormBuilder);
  private dialogRef = inject(
    MatDialogRef<ChangeUserDateSettingsModalComponent>,
  );
  private store$ = inject<Store<UserAccountSettingsState>>(Store);
  public data = inject<{
    user: UserModels.UserDocument;
    settings: UserSettingsModels.UserSettingsDocument;
  }>(MAT_DIALOG_DATA);

  public timeZones = moment.tz.names();
  public dateFormat = Object.entries(uiUserSettingsDateFormatMap).map(
    ([value, name]) => ({
      name,
      value: Number(value),
    }),
  );

  public formGroup = this.fb.nonNullable.group({
    dateFormat: [0, [Validators.required.bind(Validators)]],
    hoursFormat: [0, [Validators.required.bind(Validators)]],
    timezone: '',
  });

  protected readonly hourFormat = {
    hours24: 0,
    hours12: 1,
  };

  public ngOnInit(): void {
    const userSettings = this.data?.settings;
    if (userSettings) {
      this.formGroup.patchValue({
        dateFormat: userSettings.dateFormat,
        hoursFormat: userSettings.hoursFormat,
        timezone: this.data.user?.timezone ?? '',
      });
    }
  }

  public close(): void {
    this.dialogRef.close();
  }

  public save(): void {
    const formValue = this.formGroup.value;
    this.store$.dispatch(
      UserSettingsActions.startUpdateUserSettingsProcess({
        userSettings: {
          dateFormat: Number(formValue.dateFormat),
          hoursFormat: Number(formValue.hoursFormat),
        },
      }),
    );

    const timezone = formValue.timezone?.trim();
    if (timezone) {
      this.store$.dispatch(UserActions.setTimezone({ timezone }));
      this.store$.dispatch(
        UserActions.startUpdateUserProfile({ data: { timezone } }),
      );
    }

    this.close();
  }
}
